/**
 * CMS Constants
 *
 * Constants related to CMS components and feature flags.
 * Provides a single source of truth for values used across the CMS implementation.
 */

import { BannerType } from '@/@cms/types/entities';

/**
 * Component types as they appear in Strapi components
 */
export const COMPONENT_TYPES = {
    /** Banner component */
    BANNER: 'home.banner',
    /** Title component */
    TITLE: 'home.title',
    /** Card component */
    CARD: 'card',
    /** Section component */
    SECTION: 'section',
} as const;

/**
 * Feature flag values
 */
export const FEATURE_FLAGS = {
    /** Indicates no feature flag */
    NONE: 'None',
    /** Alpha status flag */
    ALPHA: 'Alpha',
    /** Beta status flag */
    BETA: 'Beta',
    /** Test flag */
    TEST: 'test',
} as const;

/**
 * Flag status values for components
 */
export const FLAG_STATUS = {
    /** No flag status */
    NONE: 'None',
    /** Alpha status */
    ALPHA: 'Alpha',
    /** Beta status */
    BETA: 'Beta',
    /** New feature status */
    NEW: 'New',
} as const;

/**
 * Visibility types
 */
export const VISIBILITY_TYPES = {
    /** Publicly visible */
    PUBLIC: 'Public',
    /** Internal users only */
    INTERNAL: 'Internal',
    /** Specific client only */
    CLIENT: 'Client',
} as const;

/**
 * Banner types for CMS banners
 */
export const BANNER_STYLES = {
    [BannerType.WARNING]: {
        card: 'bg-yellow-500 text-black',
        icon: 'text-black',
        close: 'text-black/80 hover:text-black',
        link: 'text-black hover:underline',
    },
    [BannerType.DANGER]: {
        card: 'bg-red-600 text-white',
        icon: 'text-white',
        close: 'text-white/80 hover:text-white',
        link: 'text-white hover:underline',
    },
    [BannerType.ANNOUNCEMENT]: {
        card: 'bg-primary-500 text-white',
        icon: 'text-white',
        close: 'text-white/80 hover:text-white',
        link: 'text-white hover:underline',
    },
} as const;

/**
 * Custom placeholder patterns for templating
 */
export const TEMPLATE_PATTERNS = {
    /** User name placeholder */
    USER_NAME: '{{user.name}}',
    /** Studio URL placeholder */
    STUDIO_URL: '{{studio}}',
    /** Portal URL placeholder */
    PORTAL_URL: '{{portal}}',
} as const;

/**
 * Default values for components when not specified
 */
export const DEFAULT_VALUES = {
    /** Default card title */
    CARD_TITLE: 'Untitled Card',
    /** Default section name */
    SECTION_NAME: 'Untitled Section',
    /** Default body text */
    BODY_TEXT: '',
    /** Default feature flag */
    FEATURE_FLAG: FEATURE_FLAGS.NONE,
    /** Default flag status */
    FLAG: FLAG_STATUS.NONE,
} as const;

/**
 * Error messages for CMS component validation
 */
export const ERROR_MESSAGES = {
    /** Invalid component type */
    INVALID_COMPONENT: 'Invalid component type',
    /** Invalid Slug */
    INVALID_SLUG: 'No navigation page found for slug',
    /** Missing required field */
    MISSING_FIELD: (field: string) => `Missing required field: ${field}`,
    /** Invalid data structure */
    INVALID_DATA: 'Invalid data structure',
    /** API error */
    API_ERROR: (message: string) => `API Error: ${message}`,
} as const;

/**
 * Storage key patterns for browser storage
 */
export const STORAGE_KEYS = {
    /** Banner dismissal key pattern */
    BANNER_DISMISSED: (id: number, name: string) =>
        `banner_dismissed_${id}_${name.slice(0, 20).replace(/\s+/g, '_')}`,
} as const;
